<template>
  <ValidationObserver ref="nbRnWizard">
    <div class="card card-custom card-transparent">
    <div class="card-body p-0">
      <div class="px-8 pt-4 px-lg-10 d-flex flex-wrap">
        <button
          class="btn btn-sm btn-light-primary font-weight-bold text-uppercase mr-2 mb-2"
          @click.prevent="$router.push({ path: '/workbench', query: { lane: 'posting' } })"
        >
          ← Work Queue
        </button>
        <button
          v-if="hubClientId"
          class="btn btn-sm btn-light font-weight-bold text-uppercase mb-2"
          @click.prevent="$router.push(`/workbench/clients/${hubClientId}`)"
        >
          Open client
        </button>
      </div>
      <!--begin: Wizard-->
      <div class="wizard wizard-4" id="kt_wizard_v4" data-wizard-state="step-first" data-wizard-clickable="true">
        <!--begin: Wizard Nav-->
        <div class="wizard-nav" v-show="appData">
          <div class="wizard-steps">
            <div class="wizard-step" data-wizard-type="step" v-for="(wizardPage, index) in wizardPages" :key="index">
              <div class="wizard-wrapper">
                <div class="wizard-number">{{ wizardPage.id }}</div>
                <div class="wizard-label"><div class="wizard-title">{{ wizardPage.title }}</div>
                  <div v-if="wizardPage.subtitle" class="wizard-desc">{{ wizardPage.subtitle }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!--end: Wizard Nav -->

        <!--begin: Wizard Body-->
        <div class="card card-custom card-shadowless rounded-top-0">
          <div class="card-body p-0">
            <div class="row justify-content-center py-8 px-8 py-lg-15 px-lg-10">
              <div class="col-xl-12 col-xxl-10">
                <!--begin: Wizard Form-->
                <form class="form mt-0 mt-lg-10" id="kt_form">
                  <!-- ERROR ALERTS -->
                  <b-alert v-if="this.errorAlert" variant="warning" fade show>
                    <div class="text-dark" v-html="errorAlert"></div>
                  </b-alert>

                  <!--begin: Wizard Step 1-->
                  <div class="pb-5" data-wizard-type="step-content" data-wizard-state="current">
                    <div v-if="appData">
                      <review-page
                          :app-data="appData" :comparison-data="comparisonData" :is-read-only="isWizardReadOnly"
                          :ibais-memo-number="ibaisMemoNumber" :loading-comparison-data="loadingComparisonData"
                          @acceptForm="checkClientAndAcceptForm" @voidForm="voidForm" @unvoidForm="unvoidForm"
                      />
                    </div>
                  </div>
                  <!--end: Wizard Step 1-->

                  <!--begin: Wizard Step 2-->
                  <div class="pb-5" data-wizard-type="step-content">
                    <div v-if="appData">
                      <inclusions-page
                          :app-data="appData" :is-read-only="isWizardReadOnly"
                          :selected-inclusions="selectedInclusions"
                          :filtered-inclusions="filteredInclusions"
                          @processInclusionsBasedOnBusinessActivities="processInclusions"
                          @onUpdate="updateInclusions"
                          @setInclusionsChanged="setInclusionsChanged"
                      />
                    </div>
                  </div>
                  <!--end: Wizard Step 2-->

                  <!--begin: Wizard Step 3-->
                  <div class="pb-5" data-wizard-type="step-content">
                    <div v-if="appData && referenceData">
                      <business-locations-page
                          :app-data="appData" :ref-data="referenceData" :is-read-only="isWizardReadOnly"
                          @saveBusinessLocations="saveBusinessLocations"
                      />
                    </div>
                  </div>
                  <!--end: Wizard Step 3-->

                  <!--begin: Wizard Step 4-->
                  <div class="pb-5" data-wizard-type="step-content">
                    <div v-if="appData && referenceData">
                      <fleet-page
                          :app-data="appData" :ref-data="referenceData" :is-read-only="isWizardReadOnly"
                          @uploadFleetFiles="confirmNewFleetUpload"
                          @refreshFleetStatus="refreshFleetStatus"
                          @downloadTransactionFleet="downloadTransactionFleet"
                          @saveTransactionFleet="saveTransactionFleet"
                      />
                    </div>
                  </div>
                  <!--end: Wizard Step 4-->

                  <!--begin: Wizard Step 5-->
                  <div class="pb-5" data-wizard-type="step-content">
                    <div v-if="appData && referenceData">
                      <premium-page
                        :app-data="appData" :ref-data="referenceData" :is-read-only="isWizardReadOnly"
                        :expand-all-sections="premiumPageExpandAllSections"
                        @getVehicleSummaryFromFleetDeclaration="getVehicleSummaryFromFleetDeclaration"
                        @calculateFleetHistoryTotals="calculateFleetHistoryTotals"
                        @calculateVehicleTypesTotals="calculateVehicleTypesTotals"
                        @calculateVehiclePremiumTotals="calculateVehiclePremiumTotals"
                        @calculateStateSummaryTotals="calculateStateSummaryTotals"
                        @calculateBusinessActivityPercentTotal="calculateBusinessActivityPercentTotal"
                        @calculatePostcodeDistributionTotal="calculatePostcodeDistributionTotal"
                        @calculateClaimsHistoryTotals="calculateClaimsHistoryTotals"
                        @calculateTotalFees="calculateTotalFees"
                        @calculatePremiums="calculatePremiums"
                        @recalculateQuotePremiums="recalculateQuotePremiums"
                        @copyFeesToOtherQuotes="copyFeesToOtherQuotes"
                        @calculatePremiumTotalsUwOverride="calculatePremiumTotalsUwOverride"
                        @savePremiumPage="savePremiumPage"
                        @refreshClaimsHistoryStatus="refreshClaimsHistoryStatus"
                        @uploadClaimsHistoryFiles="uploadClaimsHistoryFiles"
                        @reloadClaimsHistoryFromClaimsCRM="reloadClaimsHistoryFromClaimsCRM"
                        @refreshUwApprovals="refreshUwApprovals"
                        @approveApproval="approveApproval"
                        @rejectApproval="rejectApproval"
                        @handleError="handleError"
                      />
                    </div>
                  </div>
                  <!--end: Wizard Step 5-->

                  <!--begin: Wizard Step 6-->
                  <div class="pb-5" data-wizard-type="step-content">
                    <finalisation-page
                        :app-data="appData" :is-read-only="isWizardReadOnly" :ibais-memo-number="ibaisMemoNumber" :outcome="outcome"
                        @createQuoteDocument="createQuoteDocument"
                        @getIbaisInclusionsText="getIbaisInclusionsText"
                        @setOutcome="setOutcome"
                        @setMemoNumber="setMemoNumber"
                        @bind-cover="completeTransaction"
                        @handleError="handleError"
                    />
                  </div>
                  <!--end: Wizard Step 6-->

                  <!--begin: Wizard Step 7-->
                  <div class="pb-5" data-wizard-type="step-content">
                    <processing-finalisation-page
                        :ledger-row-id="billingLedgerRowId"
                        :transaction-id="billingTransactionId"
                        :resolution-error="billingLedgerResolutionError"
                    />
                  </div>
                  <!--end: Wizard Step 7-->

                  <!--begin: Wizard Actions -->
                  <div class="d-flex justify-content-between border-top pt-10">
                    <button class="btn btn-light-primary font-weight-bold text-uppercase px-9 py-4"
                      data-wizard-type="action-prev" v-show="appData"
                    >
                      Previous
                    </button>
                    <button
                        class="btn btn-primary font-weight-bold text-uppercase px-9 py-4 mr-10"
                        v-show="appData && isFormAccepted && !isWizardReadOnly"
                        @click.prevent="saveAppData"
                    >
                      Save Progress
                    </button>

                    <button
                      class="btn btn-outline-danger font-weight-bold text-uppercase px-9 py-4 mr-10"
                      v-show="appData && isFormAccepted && !isWizardReadOnly"
                      @click.prevent="confirmDeclineTransaction"
                    >
                      Decline
                    </button>

                    <!-- Reopen is bind-lifecycle only (Zoho K_Status Completed → In Progress).
                         Hide on step 7 Processing: that step needs Completed + Accepted for
                         Stamp / schedule / Insight post. Keep the same red style on steps 1–6. -->
                    <div v-if="appData && isFormAccepted && isWizardReadOnly && !isProcessingFinalisationStep">
                      <button class="btn btn-danger font-weight-bold text-uppercase px-9 py-4 mr-10"
                              @click.prevent="confirmReopenTransaction">
                        Reopen&nbsp;Transaction
                      </button>
                      <button class="btn btn-lg btn-outline-primary ml-5" title="Save Premium Page PDF"
                              @click.prevent="savePremiumPage">
                        Save Premium Page PDF
                      </button>
                    </div>
                    <div v-else-if="appData && isFormAccepted && isWizardReadOnly && isProcessingFinalisationStep">
                      <button class="btn btn-lg btn-outline-primary ml-5" title="Save Premium Page PDF"
                              @click.prevent="savePremiumPage">
                        Save Premium Page PDF
                      </button>
                    </div>

                    <div>
                      <div v-if="nextButtonError" class="text-danger font-weight-bold">{{ nextButtonError }}</div>
                      <button class="btn btn-primary font-weight-bold text-uppercase px-9 py-4"
                              v-show="appData && isFormAccepted && !isLastPage" @click.prevent="goToNextPage"
                      >
                        Next&nbsp;Step
                      </button>
                    </div>

                    <!--
                      Do NOT use data-wizard-type="action-submit": Metronic wizard CSS
                      hides action-submit on first/between steps and only shows it on last.
                      Combined with the old !isLastPage v-show, Bind Cover was never visible
                      after Reopen (step 6 CSS-hidden, step 7 Vue-hidden).
                    -->
                    <button
                      class="btn btn-success font-weight-bold text-uppercase px-9 py-4"
                      v-show="appData && !isWizardReadOnly"
                      @click.prevent="completeTransaction"
                      :disabled="!isCompleteTransactionButtonEnabled()"
                    >
                      Bind&nbsp;Cover
                    </button>

                    <b-modal
                      ref="declineTransactionModal" title="Decline Transaction" size="md"
                      no-close-on-backdrop hide-header-close
                      @ok="declineTransaction" @cancel="resetFormModals"
                    >
                      <div>
                        <b-card-text>Enter the reason for declining the transaction:</b-card-text>
                        <div class="form-group row mb-0">
                          <b-form-textarea size="2000" class="form-control" v-model="declinationReason"/>
                        </div>
                        <b-card-text v-if="formModalError" class="text-danger mt-3">{{ formModalError }}</b-card-text>
                      </div>
                    </b-modal>

                    <b-modal ref="claimsHistoryModal" title="Claims History" size="lg"
                             scrollable no-close-on-backdrop hide-header-close ok-only
                             @hidden="clearClaimsHistory"
                    >
                      <div v-if="Array.isArray(tempClaimsHistory) && tempClaimsHistory.length > 0">
                        <table class="table table-sm table-bordered">
                          <thead>
                            <th width="16%" class="text-center">Claim Number</th>
                            <th width="16%" class="text-center">Date of Loss</th>
                            <th width="16%" class="text-center">Notification Date</th>
                            <th width="16%" class="text-center">Claim Excess</th>
                            <th width="16%" class="text-center">Net Incurred</th>
                            <th width="16%" class="text-center">Cause Code</th>
                          </thead>
                          <tr v-for="(claim, index) in tempClaimsHistory" :key="index">
                            <td class="text-center">{{ claim.claimNumber }}</td>
                            <td class="text-center">{{ claim.dateOfLoss }}</td>
                            <td class="text-center">{{ claim.notificationDate }}</td>
                            <td>{{ claim.claimExcess | currency("$", ",", 0, ".", "front", true)}}</td>
                            <td>{{ claim.netIncurred | currency }}</td>
                            <td>{{ claim.causeCode }}</td>
                          </tr>
                        </table>
                      </div>
                    </b-modal>
                  </div>
                  <!--end: Wizard Actions -->
                </form>
                <!--end: Wizard Form-->
              </div>
            </div>
          </div>
        </div>
        <!--end: Wizard Bpdy-->
      </div>
      <!--end: Wizard-->
    </div>
  </div>
  </ValidationObserver>
</template>

<style lang="scss">
@import "@/assets/sass/pages/wizard/wizard-4.scss";

.page-loading {
  display: flex;
  justify-content: center;
  align-items: center;
}

.step-disabled {
  cursor: not-allowed !important;
}

body.swal2-shown > [aria-hidden="true"] {
  transition: 0.1s filter;
  filter: blur(10px);
}
</style>

<script>
import _ from "lodash";
import FormData from "form-data";
import Swal from "sweetalert2";
import stringify from "fast-json-stable-stringify";
import KTUtil from "@/assets/js/components/util";
import KTWizard from "@/assets/js/components/wizard";
import ApiService from "@/core/services/api.service";
import BrandService from "@/core/services/brand.service";
import ReviewPage from "@/view/pages/wizard/newbusiness-renewal/pages/1-ReviewPage.vue";
import InclusionsPage from "@/view/pages/wizard/newbusiness-renewal/pages/2-InclusionsPage.vue";
import BusinessLocationsPage from "@/view/pages/wizard/newbusiness-renewal/pages/3-BusinessLocationsPage.vue";
import FleetPage from "@/view/pages/wizard/newbusiness-renewal/pages/4-FleetPage.vue";
import PremiumPage from "@/view/pages/wizard/newbusiness-renewal/pages/5-PremiumPage.vue";
import FinalisationPage from "@/view/pages/wizard/newbusiness-renewal/pages/6-FinalisationPage.vue";
import ProcessingFinalisationPage from "@/view/pages/wizard/newbusiness-renewal/pages/7-ProcessingFinalisationPage.vue";

import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";

export default {
  name: "NewBusinessRenewal",
  components: {
    BusinessLocationsPage,
    ReviewPage, InclusionsPage, PremiumPage, FleetPage, FinalisationPage, ProcessingFinalisationPage
  },

  data() {
    return {
      wizardPages: [
        { id: 1, title: "Client & Proposal" },
        { id: 2, title: "Policy Inclusions" },
        { id: 3, title: "Business Locations" },
        { id: 4, title: "Fleet" },
        { id: 5, title: "Premium Details" },
        { id: 6, title: "Quote and Bind" },
        { id: 7, title: "Processing and Finalisation" }
      ],

      transactionId: null,
      formId: null,
      pageNumber: null,
      queryLedgerRowId: null,
      wizardObj: null,
      errorAlert: null,
      nextButtonError: null,
      appData: null,
      appDataString: null,
      referenceData: null,
      tempClaimsHistory: null,
      uploadingClaimsHistory: false,

      loadingComparisonData: false,
      comparisonData: null,

      formModalError: null,

      outcome: null,
      outcomes: {
        acceptedByClient: "Accepted by Client",
        rejectedByClient: "Rejected by Client",
        declinedByInsurer: "Declined by Insurer",
      },

      ibaisMemoNumber: null,
      declinationReason: null,

      selectedInclusions: {
        policyConditions: [],
        excesses: [],
        generalBenefits: [],
      },
      deletedInclusions: [],
      allInclusions: {
        policyConditions: [],
        excesses: [],
        generalBenefits: [],
      },
      filteredInclusions: {
        policyConditions: [],
        excesses: [],
        generalBenefits: [],
      },
      selectedInclusionsChanged: false
    };
  },

  mounted() {
    // Show page loading
    this.startPageLoading();

    // Extract information from URL params
    const queryParams = this.$route.query;
    this.formId = queryParams.formId || null;
    this.transactionId = queryParams.transactionId || null;
    this.queryLedgerRowId = queryParams.ledgerRowId || null;
    this.pageNumber = queryParams.pageNumber || null;
    if(!_.isNil(this.pageNumber)) {
      this.pageNumber = Number(this.pageNumber);
    }
    
    // Get Application Data
    this.getAppData();
  },

  computed: {
    isWizardReadOnly: function () {
      if(this.appData) {
        let txnStatus = _.get(this.appData, 'transaction.K_Status');
        return txnStatus === "Completed";
      }
      return true;
    },

    isBrandFuse: function () {
      const brand = this.appData?.transaction?.K_Brand;
      return brand === 'Fuse' || brand === 'Fuse MSI';
    },

    hubClientId: function () {
      const fromQuery = this.$route.query.from;
      if (typeof fromQuery === "string" && fromQuery.indexOf("client:") === 0) {
        return fromQuery.slice("client:".length);
      }
      return _.get(this.appData, "transaction.K_Client.id")
        || _.get(this.appData, "client.id")
        || null;
    },

    isFormAccepted: function () {
      if(this.appData) {
        let formStatus = _.get(this.appData, 'form.K_Status');
        return formStatus === "Accepted";
      }
      return false;
    },

    isFormVoided: function () {
      if(this.appData) {
        let formStatus = _.get(this.appData, 'form.K_Status');
        return formStatus === "Voided";
      }
      return false;
    },

    isFormForRenewal: function() {
      if(this.appData) {
        let formType = _.get(this.appData, 'form.K_FormType');
        return formType === "Renewal Declaration Form";
      }
      return false;
    },

    isRenewalTransaction: function () {
      if(this.appData) {
        let txnType = _.get(this.appData, 'transaction.K_Type');
        return txnType === "Renewal";
      }
      return false;
    },

    isLastPage: function () {
      return this.wizardObj && this.wizardObj.isLastStep();
    },

    /** Step 7 — Processing and Finalisation (agency stamp / schedule / Insight post). */
    isProcessingFinalisationStep: function () {
      if (this.isLastPage) {
        return true;
      }
      if (_.isNumber(this.pageNumber) && Number(this.pageNumber) === 7) {
        return true;
      }
      try {
        if (this.wizardObj && typeof this.wizardObj.getStep === 'function') {
          return Number(this.wizardObj.getStep()) === 7;
        }
      } catch (_err) {
        // ignore
      }
      return false;
    },

    billingLedgerRowId: function () {
      if (this.queryLedgerRowId) {
        return String(this.queryLedgerRowId);
      }
      if (this.appData && this.appData.billingLedgerRowId) {
        return String(this.appData.billingLedgerRowId);
      }
      return null;
    },

    billingTransactionId: function () {
      return this.transactionId || (this.appData && this.appData.transaction && this.appData.transaction.id) || null;
    },

    billingLedgerResolutionError: function () {
      if (this.billingLedgerRowId) {
        return null;
      }
      return (this.appData && this.appData.billingLedgerResolutionError) || null;
    },

    premiumPageExpandAllSections: function () {
      return _.isNumber(this.pageNumber) && this.pageNumber === 5;
    },

    uwApprovalsCompleted: function() {
      if(this.appData) {
        const approvals = this.appData.uwApprovals;
        if(Array.isArray(approvals) && approvals.length > 0) {
          return !_.some(approvals, approval => {
            return approval.K_ApprovalStatus !== 'Approved';
          })
        }
        else {
          return true;
        }
      }
      return false;
    },

    acceptedQuote: function () {
      const quotes = _.get(this.appData, 'quotes');
      if(Array.isArray(quotes) && quotes.length > 0) {
        return _.find(quotes, { 'K_QuoteStatus': 'Accepted' });
      }
      return null;
    },

    fleetLookupRunning: function () {
      return this.appData?.fleetInfo?.K_FleetLookupRunning || false;
    },

    fleetExtractionRunning: function () {
      return this.appData?.fleetInfo.K_FleetExtractionRunning || false;
    },

    claimsHistoryExtractionRunning: function () {
      return this.appData?.claimsHistoryInfo?.K_ClaimsHistoryExtractionRunning || false;
    }
  },

  methods: {
    refreshAppData: function () {
      return ApiService.post("/nb/get-wizard-app-data", {
        formId: this.formId,
        transactionId: this.transactionId
      })
    },

    getAppData: function () {
      // Show page loading
      this.startPageLoading();

      this.refreshAppData()
        .then(({ data }) => {
          this.appData = data.result;
          this.appDataString = stringify(this.appData);

          // Set UI Brand
          BrandService.setUIBrand(this.appData.form?.K_Brand);

          this.calculateAllTotals();

          this.$nextTick(()=> {
            // Set wizard title and subtitle
            this.setTitles();

            // Set local variables
            if(this.isFormForRenewal) {
              this.ibaisMemoNumber = this.appData.form.K_IbaisMemoNumber;
            }

            if (this.appData && this.appData.transaction) {
              // Set transactionId
              if (!this.transactionId) {
                this.transactionId = this.appData.transaction.id;
              }

              // For Renewal transaction, pre-fill the iBais Memo Number
              if (this.isRenewalTransaction) {
                this.ibaisMemoNumber = this.appData.transaction.K_IbaisMemoNumber;
              }

              // Set outcome and reason - if available
              this.outcome = this.appData.transaction.K_Outcome || null;
              if (this.outcome === this.outcomes.acceptedByClient) {
                this.ibaisMemoNumber = this.appData.transaction.K_IbaisMemoNumber;
              }
              else if (this.outcome === this.outcomes.declinedByInsurer) {
                this.declinationReason = this.appData.transaction.K_Reason;
              }
            }

            // Set starting step for the wizard
            let startStep = 1;
            if (this.isWizardReadOnly) {
              if(this.pageNumber) {
                // Start from the given page number
                startStep = Number(this.pageNumber);
              }
              else {
                // If transaction is already completed, directly start the wizard on last page.
                startStep = this.wizardPages.length;
              }
            }
            else if (this.appData && this.pageNumber) {
              // Start from the given page number
              startStep = Number(this.pageNumber);
            }
            else if (this.isFormAccepted && this.appData && this.appData.currentPage) {
              // Check if the user was previously on a particular wizard page
              startStep = this.appData.currentPage;
            }

            // Initialise the UI wizard
            this.initWizard(startStep);

            // Get inclusions in the background
            this.getAllInclusions();

            // Get reference data in the background
            this.getReferenceData();

            // Get form-CRM-comparison data if needed
            this.getFormCRMComparisonData();

            // Stop page loading
            this.stopPageLoading();
          });
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    setTitles: function () {
      // Set wizard title and sub-title in global store so that it can be used by the header
      if (this.appData) {

        if(this.appData.user) {
          this.$store.commit("setUser", this.appData.user);
        }

        if (this.appData.policy && this.appData.transaction) {
          let transactionType = _.toUpper(this.appData.transaction.K_Type);
          let policyNumber = this.appData.policy.K_PolicyNumber;

          let title = `${transactionType} - ${policyNumber}`;
          this.$store.commit("setWizardTitle", title);

          if (this.appData.client) {
            this.$store.commit("setWizardSubtitle", this.appData.client.Name);
          }
        }
        else if (this.appData.form) {
          let formType = this.appData.form.K_FormType || 'Motor Proposal Form';
          this.$store.commit("setWizardTitle", formType);

          this.$store.commit("setWizardSubtitle", this.appData.form.K_ClientNamePerForm);
        }
      }
    },

    checkClientAndAcceptForm: function () {
      // Renewal Form
      if(this.isFormForRenewal) {
        this.acceptForm();
      }
      // New Motor Proposal Form
      else {
        const ibaisClientNumber = _.toUpper(this.appData.form.K_IbaisClientNumber);
        ApiService.get(`/mpf/client-search`,
            { params: { brand: this.appData.form.K_Brand, ibaisClientNumber: ibaisClientNumber }})
            .then(({ data }) => {
              const existingClient = data.result;

              if(existingClient && existingClient.id) {
                const message = `Existing client/prospect found in the CRM for ${ibaisClientNumber}. Some data might be copied over the existing record.<br/><br/>Are you sure you want to continue?`;
                Swal.fire({
                  titleText: `Existing Client or Prospect`,
                  html: `<p>${message}</p>`,
                  icon: "warning",
                  allowOutsideClick: false,
                  allowEnterKey: false,
                  confirmButtonText: 'Yes',
                  showConfirmButton: true,
                  showCancelButton: true,
                  showCloseButton: false
                })
                  .then(result => {
                    if(result && result.isConfirmed) {
                      this.acceptForm();
                    }
                  });
              }
              else {
                this.acceptForm();
              }
            })
            .catch((error) => {
              this.handleError(error);
            });
      }
    },

    acceptForm: function () {
      // Show page loading
      this.startPageLoading();

      let url = null;
      let formVersion = this.appData.form.K_FormVersion;
      // Old proposal form
      if(!formVersion || formVersion === '1') {
        url = `/nb/accept-motor-proposal-form`;
      }
      else {
        url = `/mpf/form/${this.formId}/accept`;
      }

      ApiService.post(url, {
        formId: this.formId,
        ibaisClientNumber: _.toUpper(this.appData.form.K_IbaisClientNumber),
        ibaisMemoNumber: _.toUpper(this.ibaisMemoNumber)
      })
        .then((/*{ data }*/) => {
          const message = `${this.isFormForRenewal ? 'Renewal' : 'Quote'} transaction has been created. `
                        + `If available, uploaded fleet file and claims-history files will be processed automatically.`;

          Swal.fire({
            titleText: `${this.appData.form.K_FormType} Accepted`,
            html: `<p class="text-justify">${message}</p><br/>
                   <span class="font-weight-bold">Please close this browser window.</span>`,
            icon: "success",
            width: '50rem',
            allowOutsideClick: false,
            allowEnterKey: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            showCancelButton: false,
            showCloseButton: false
          });
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    voidForm: function (voidReason, createCrmRecordsForVoidedForm) {
      this.startPageLoading();

      let url = null;
      let formVersion = this.appData.form.K_FormVersion;
      // Old proposal form
      if(!formVersion || formVersion === '1') {
        url = `/nb/void-motor-proposal-form`;
      }
      else {
        url = `/mpf/form/${this.formId}/void`;
      }

      ApiService.post(url, {
        formId: this.formId,
        voidReason: voidReason,
        createCrmRecords: createCrmRecordsForVoidedForm,
        ibaisClientNumber: createCrmRecordsForVoidedForm ? this.appData.form.K_IbaisClientNumber : null
      })
        .then(() => {
          this.appData = null;
          this.appDataString = null;

          this.setError("This form is now voided. Please close this window.");
          this.stopPageLoading();
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    unvoidForm: function (unvoidReason) {
      this.startPageLoading();

      let url = null;
      let formVersion = this.appData.form.K_FormVersion;
      // Old proposal form
      if(!formVersion || formVersion === '1') {
        url = `/nb/unvoid-motor-proposal-form`;
      }
      else {
        url = `/mpf/form/${this.formId}/unvoid`;
      }

      ApiService.post(url, {
        formId: this.formId,
        unvoidReason: unvoidReason
      })
        .then(() => {
          this.getAppData();
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    confirmDeclineTransaction: function () {
      this.$refs["declineTransactionModal"].show();
    },

    declineTransaction: function (bvModalEvent) {
      this.clearError();
      this.formModalError = null;
      if (!this.declinationReason) {
        this.formModalError = "Please enter the required information";
        bvModalEvent.preventDefault();
        return;
      }

      // Set outcome
      this.outcome = this.outcomes.declinedByInsurer;

      // Decline and complete the transaction
      this.completeTransaction();
    },

    confirmReopenTransaction: function () {
      const message = `Are you sure you want to reopen this transaction?`;
      Swal.fire({
        titleText: `Reopen Transaction`,
        html: `<p>${message}</p>`,
        icon: "warning",
        allowOutsideClick: false,
        allowEnterKey: false,
        confirmButtonText: 'Yes',
        showConfirmButton: true,
        showCancelButton: true,
        showCloseButton: false
      })
        .then(result => {
          if(result && result.isConfirmed) {
            this.reopenTransaction();
          }
        });
    },

    reopenTransaction: function () {
      this.clearError();
      if (!this.isWizardReadOnly) {
        return;
      }

      ApiService.post("/nb/reopen-transaction", {
        transactionId: this.transactionId
      })
          .then(() => {
            // After reopen, land on Quote and Bind (step 6). Deep-links often use pageNumber=7.
            this.pageNumber = 6;
            this.getAppData();
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    resetFormModals: function () {
      this.formModalError = null;
    },

    getAllInclusions: function () {
      if (!this.transactionId) {
        return;
      }

      // Start page loading
      this.startPageLoading();

      Promise.all([
        // Get inclusions for the transaction
        this.getInclusions(this.transactionId),

        // Get all inclusions for the brand
        this.getInclusions(),
      ]).then((values) => {
        // Save already selected transaction inclusions
        const transactionInclusions = values[0].data.result;

        this.selectedInclusions.generalBenefits = this.setInclusionBusinessActivity(transactionInclusions.generalBenefits);
        this.selectedInclusions.excesses = this.setInclusionBusinessActivity(transactionInclusions.excesses);
        this.selectedInclusions.policyConditions = this.setInclusionBusinessActivity(transactionInclusions.policyConditions);
        this.deletedInclusions = this.setInclusionBusinessActivity(transactionInclusions.deletedInclusions);

        // Save all available brand inclusions
        const brandInclusions = values[1].data.result;
        this.allInclusions.generalBenefits = this.setInclusionBusinessActivity(brandInclusions.generalBenefits);
        this.allInclusions.excesses = this.setInclusionBusinessActivity(brandInclusions.excesses);
        this.allInclusions.policyConditions = this.setInclusionBusinessActivity(brandInclusions.policyConditions);

        this.processInclusions();

        // Stop page loading
        this.stopPageLoading();
      });
    },

    getInclusions: function (transactionId) {
      let brand = this.appData.form.K_Brand || null;
      return ApiService.post("/nb/inclusions", {
        transactionId: transactionId || null,
        brand: brand,
      });
    },

    setInclusionBusinessActivity: function (inclusions) {
      const allBAs = this.appData.allBusinessActivities;

      if (!Array.isArray(inclusions)) {
        inclusions = [];
      }

      inclusions.forEach(function (inclusion) {
        const incBA = inclusion.K_BusinessActivity;

        if(incBA) {
          allBAs.forEach((ba) => {
            inclusion[ba.K_BusinessActivityKey] = (incBA.id === ba.id);
          });
        }
      });
      return inclusions;
    },

    processInclusions: function () {
      // Set filtered inclusions based on chosen business activities.
      this.filterInclusionsBasedOnBusinessActivities();

      // Remove invalid inclusions from selected inclusions (based on the newly filtered inclusions)
      this.removeInvalidInclusions(this.selectedInclusions.generalBenefits, this.filteredInclusions.generalBenefits);
      this.removeInvalidInclusions(this.selectedInclusions.excesses, this.filteredInclusions.excesses);
      this.removeInvalidInclusions(this.selectedInclusions.policyConditions, this.filteredInclusions.policyConditions);

      // Programmatically select all applicable (and non-deleted) inclusions
      const missingGeneralBenefits = this.getNotSelectedInclusions(this.selectedInclusions.generalBenefits, this.filteredInclusions.generalBenefits);
      this.selectedInclusions.generalBenefits = this.selectedInclusions.generalBenefits.concat(missingGeneralBenefits);

      const missingExcesses = this.getNotSelectedInclusions(this.selectedInclusions.excesses, this.filteredInclusions.excesses);
      this.selectedInclusions.excesses = this.selectedInclusions.excesses.concat(missingExcesses);

      const missingPolicyConditions = this.getNotSelectedInclusions(this.selectedInclusions.policyConditions, this.filteredInclusions.policyConditions);
      this.selectedInclusions.policyConditions = this.selectedInclusions.policyConditions.concat(missingPolicyConditions);

      this.selectedInclusionsChanged = true;
    },

    filterInclusionsBasedOnBusinessActivities: function () {
      let generalBenefits = [];
      let excesses = [];
      let policyConditions = [];

      if(this.isBrandFuse || !this.appData.transaction.selectedBusinessActivities) {
        generalBenefits = this.allInclusions.generalBenefits || [];
        excesses = this.allInclusions.excesses || [];
        policyConditions = this.allInclusions.policyConditions || [];
      }
      else {
        // For all chosen business activities (i.e. value=true), get their inclusions and add to filtered inclusions.
        for (const [key, value] of Object.entries(this.appData.transaction.selectedBusinessActivities)) {
          if (value === true) {
            generalBenefits = generalBenefits.concat(
                _.filter(this.allInclusions.generalBenefits, function (inclusion) {
                  return inclusion[key] === true;
                })
            );

            excesses = excesses.concat(
                _.filter(this.allInclusions.excesses, function (inclusion) {
                  return inclusion[key] === true;
                })
            );

            policyConditions = policyConditions.concat(
                _.filter(this.allInclusions.policyConditions, function (inclusion) {
                  return inclusion[key] === true;
                })
            );
          }
        }
      }

      this.filteredInclusions.generalBenefits = generalBenefits;
      this.filteredInclusions.excesses = excesses;
      this.filteredInclusions.policyConditions = policyConditions;
    },

    removeInvalidInclusions: function (selectedInclusions, filteredInclusions) {
      _.remove(selectedInclusions, (inclusion) => {
        return !_.some(filteredInclusions, { Name: inclusion.Name });
      });
    },

    getNotSelectedInclusions: function (selectedInclusions, filteredInclusions) {
      let missingInclusions = _.filter(filteredInclusions, (inclusion) => {
        return (
          !_.some(selectedInclusions, { Name: inclusion.Name }) &&
          !_.some(this.deletedInclusions, { Name: inclusion.Name })
        );
      });
      return Array.isArray(missingInclusions) ? missingInclusions : [];
    },

    getDeletedInclusions: function (selectedInclusions, filteredInclusions) {
      let deletedInclusions = _.filter(filteredInclusions, (inclusion) => {
        return !_.some(selectedInclusions, { Name: inclusion.Name });
      });
      return Array.isArray(deletedInclusions) ? deletedInclusions : [];
    },

    updateInclusions: function (inclusionKey, inclusions) {
      this.selectedInclusions[inclusionKey] = inclusions;
      this.selectedInclusionsChanged = true;

      // Re-calculate deleted inclusions
      let deletedInclusions = [];
      deletedInclusions = deletedInclusions.concat(this.getDeletedInclusions(this.selectedInclusions.generalBenefits, this.filteredInclusions.generalBenefits));
      deletedInclusions = deletedInclusions.concat(this.getDeletedInclusions(this.selectedInclusions.excesses, this.filteredInclusions.excesses));
      deletedInclusions = deletedInclusions.concat(this.getDeletedInclusions(this.selectedInclusions.policyConditions, this.filteredInclusions.policyConditions));
      this.deletedInclusions = deletedInclusions;
    },

    setInclusionsChanged: function () {
      this.selectedInclusionsChanged = true;
    },

    getReferenceData: function () {
      if (!this.transactionId) {
        return;
      }

      ApiService.post("/nb/ref-data")
          .then(({ data }) => {
            this.referenceData = data.result;
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    getFormCRMComparisonData: function () {
      if (!this.formId || this.isFormAccepted || this.isFormVoided) {
        return;
      }

      // Get the comparison data only for Form v2
      let formVersion = _.get(this.appData, 'form.K_FormVersion');
      if(formVersion && formVersion !== '1') {
        this.loadingComparisonData = true;
        ApiService.get(`/nb/form-compare/${this.formId}`)
          .then(({ data }) => {
            this.comparisonData = data.result;
            this.loadingComparisonData = false;
          })
          .catch((error) => {
            this.loadingComparisonData = false;
            this.handleError(error);
          });
      }
    },

    createQuoteDocument: function (uiQuoteDocument) {
      this.clearError();
      if (this.isWizardReadOnly) {
        return;
      }
      this.appData.documents.quoteDocument = null;
      this.appData.quoteDocumentSent = false;

      this.outcome = null;
      this.appData.transaction.K_Reason = null;
      this.declinationReason = null;

      // Show doc processing
      uiQuoteDocument.processing = true;

      this.saveToServer()
          .then(() => {
            ApiService.post("/nb/create-document", {
              documentType: uiQuoteDocument.docgenDocType,
              transactionId: this.transactionId
            })
                .then(({ data }) => {
                  this.appData.documents[uiQuoteDocument.docType] = data.result;

                  this.saveAppData(true);

                  uiQuoteDocument.processing = false;
                  this.stopPageLoading();
                })
                .catch((error) => {
                  uiQuoteDocument.processing = false;

                  this.handleError(error);
                });
          })
          .catch((error) => {
            uiQuoteDocument.processing = false;
            this.handleError(error);
          });
    },

    setOutcome: function (outcome) {
      this.outcome = outcome;
    },

    setMemoNumber: function (memoNumber) {
      this.ibaisMemoNumber = memoNumber;
    },

    isQuoteUwOverridden: function (quoteIndex) {
      const quote = _.nth(this.appData.quotes, quoteIndex);
      return quote ? quote.K_UwPremiumOverride : false;
    },

    getIbaisInclusionsText: function () {
      this.startPageLoading();

      this.saveToServer()
          .then(() => {
            ApiService.get(`/nb/${this.transactionId}/ibais-policy-inclusions`)
                .then(({ data }) => {
                  // Create a new Blob object with the text content and set its MIME type to plain text.
                  const blob = new Blob([data.result], { type: "text/plain" });

                  const link = document.createElement("a");
                  link.download = `iBais-Inclusions-Text.txt`;
                  link.href = window.URL.createObjectURL(blob);
                  document.body.appendChild(link);

                  link.click();

                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(link.href);

                  this.stopPageLoading();
                })
                .catch((error) => {
                  this.handleError(error);
                });
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    initWizard: function (startStep) {
      // Initialize form wizard
      const wizard = new KTWizard("kt_wizard_v4", {
        startStep: 1, // initial active step number
        clickableSteps: true, // allow step clicking
      });

      this.wizardObj = wizard;

      if (startStep !== 1) {
        setTimeout(function () {
          wizard.goTo(startStep, true);
        }, 0);
      }

      // // Before-Next event - Validation before going to next page
      // wizard.on("beforeNext", (wizardObj, nextPage) => {
      // });

      // Before-Prev event - Validation before going to previous page
      wizard.on("beforePrev", (wizardObj, prevPage) => {
        this.clearError();

        // Save current page number in app-data.
        if (this.appData) {
          this.appData.currentPage = prevPage;

          // Save app-data to server
          this.saveAppData();
        }
      });

      // // Before-Change event - Save app-data and inclusions to server
      // wizard.on("beforeChange", (wizardObj, newPage) => {
      // });

      // Change event - Scroll to top of the page
      wizard.on("change", (/*wizardObj*/) => {
        setTimeout(function () {
          KTUtil.scrollTop();
        }, 500);
      });
    },

    saveAppData: function (noSpinner) {
      if (this.isWizardReadOnly) {
        return;
      }

      // Save app-data to the server (only if it has changed)
      let currentAppDataString = stringify(this.appData);
      if (currentAppDataString !== this.appDataString) {
        if (noSpinner !== true) {
          this.startPageLoading();
        }

        this.saveToServer()
          .then(() => {
            this.appDataString = currentAppDataString;

            // Save inclusions to the server if selection has changed
            if (this.selectedInclusionsChanged) {
              let inclusionsData = {
                generalBenefits: this.selectedInclusions.generalBenefits,
                excesses: this.selectedInclusions.excesses,
                policyConditions: this.selectedInclusions.policyConditions,
                deletedInclusions: this.deletedInclusions,
              };

              ApiService.post("/nb/save-inclusions", {
                transactionId: this.transactionId,
                inclusionsData: inclusionsData
              }).then(() => {
                // Reset the flag
                this.selectedInclusionsChanged = false;
                // Stop page loading
                this.stopPageLoading();
              });
            } else {
              // Stop page loading
              this.stopPageLoading();
            }
          })
          .catch((error) => {
            this.handleError(error);
          });
      }
    },

    saveToServer: function () {
      let currentAppDataString = stringify(this.appData);
      if (currentAppDataString !== this.appDataString) {
        // Wizard-app-data has changed - save to server
        return ApiService.post("/nb/save-wizard-app-data", {
          transactionId: this.transactionId,
          appData: this.appData
        });
      }
      // Just return an empty resolved promise as no saving is needed.
      return Promise.resolve();
    },

    saveQuote: function (quote) {
      return ApiService.post("/nb/save-quote", {
        transactionId: this.transactionId,
        quote: quote
      });
    },

    isCompleteTransactionButtonEnabled: function () {
      if (this.isWizardReadOnly || !this.outcome) {
        return false;
      }

      if (this.outcome === this.outcomes.acceptedByClient && this.ibaisMemoNumber && this.acceptedQuote) {
        return true;
      }
      else if (this.outcome === this.outcomes.rejectedByClient && this.appData.transaction.K_Reason) {
        return true;
      }
      else if (this.outcome === this.outcomes.declinedByInsurer && this.declinationReason) {
        return true;
      }
      return false;
    },

    completeTransaction: function () {
      this.clearError();
      if (this.isWizardReadOnly) {
        return;
      }

      if (this.outcome === this.outcomes.acceptedByClient) {
        if(!this.appData.uwApprovalsDataValid) {
          this.setError('Invalid UW-Approvals - Please click the \'Refresh Approvals\' button to refresh the data.');
          return;
        }

        if(!this.uwApprovalsCompleted) {
          this.setError('All Approvals must be completed before proceeding further.');
          return;
        }
      }

      // Show page loading
      this.startPageLoading();

      let outcomeData = null;
      let acceptedQuoteId = null;
      if (this.outcome === this.outcomes.acceptedByClient) {
        outcomeData = this.ibaisMemoNumber;
        acceptedQuoteId = this.acceptedQuote.id;
      }
      else if (this.outcome === this.outcomes.rejectedByClient) {
        outcomeData = this.appData.transaction.K_Reason;
      }
      else if (this.outcome === this.outcomes.declinedByInsurer) {
        outcomeData = this.declinationReason;
      }

      ApiService.post("/nb/complete-transaction", {
        transactionId: this.transactionId,
        transactionType: this.appData.transaction.K_Type,
        outcome: this.outcome,
        outcomeData: outcomeData,
        acceptedQuoteId: acceptedQuoteId
      })
        .then(() => {
          this.refreshAppData()
              .then(({ data }) => {
                this.appData = data.result;
                this.appDataString = stringify(this.appData);

                this.calculateAllTotals();

                if (this.outcome === this.outcomes.acceptedByClient && this.wizardObj) {
                  this.wizardObj.goTo(this.wizardPages.length, false);
                }
                this.stopPageLoading();
              })
              .catch((error) => {
                this.handleError(error);
              });

          // // Set transaction as completed
          // this.appData.transaction.K_Status = "Completed";
          //
          // // Go to last page
          // let currentStep = this.wizardObj.getStep();
          // let lastStep = this.wizardPages.length;
          // if (currentStep !== lastStep) {
          //   this.wizardObj.goTo(lastStep, false);
          // }
          //
          // setTimeout(() => {
          //   // Stop page loading
          //   this.stopPageLoading();
          //
          //   KTUtil.scrollTop();
          // }, 0);
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    goToNextPage: async function () {
      this.clearError();

      let isNavAllowed = true;
      let currentStep = this.wizardObj.getStep();
      if(!this.isWizardReadOnly) {
        isNavAllowed = await this.isPageValid(currentStep);
      }

      if(isNavAllowed) {
        // Save current page number in app-data.
        this.appData.currentPage = currentStep + 1;

        // Save app-data to server
        this.saveAppData();

        this.wizardObj.goNext();
      }
      else {
        this.nextButtonError = `Please check all sections for errors.`;
      }
    },

    isPageValid: async function(pageNumber) {
      let isValid = false;
      if(!this.$refs || !this.$refs.nbRnWizard || !this.appData){
        return isValid;
      }

      let page = _.find(this.wizardPages, { 'id': pageNumber });
      if(!page) {
        console.log(`Invalid page number ${pageNumber}`);
        return isValid;
      }

      let wizardPageId = `wizardPage${page.id}`;
      let isDevelopment = process.env.NODE_ENV === 'development';

      let formChildren = this.$refs.nbRnWizard.$children;
      if(Array.isArray(formChildren)) {
        let currentPageComponent = _.find(formChildren, child => {
          return child.$el && child.$el.id === wizardPageId;
        });
        if(!currentPageComponent) {
          if(isDevelopment) {
            console.log(`Could not find currentPageComponent for id ${wizardPageId}`);
          }
        }
        else {
          let currentPageRef = currentPageComponent.$refs[wizardPageId];
          if(!currentPageRef) {
            if(isDevelopment) {
              console.log(`Could not find currentPageRef for id ${wizardPageId}`);
            }
          }
          else {
            isValid = await currentPageRef.validate();
            if(isValid && typeof currentPageComponent.validateAdditional === 'function') {
              isValid = await currentPageComponent.validateAdditional();
            }
            if(!isValid && isDevelopment) {
              console.log(currentPageRef.errors);
            }
          }
        }
      }
      return isValid;
    },

    getVehicleSummaryFromFleetDeclaration: function () {
      this.clearError();
      if (this.isWizardReadOnly) {
        return;
      }

      // Show page loading
      this.startPageLoading();

      this.saveToServer()
        .then(() => {
          ApiService.post(`/nb/fleet-vehicle-summary`, {
            transactionId: this.transactionId
          })
            .then(() => {
              this.refreshAppData()
                  .then(({ data }) => {
                    this.appData = data.result;
                    this.appDataString = stringify(this.appData);

                    this.calculateAllTotals();

                    this.stopPageLoading();
                  })
                  .catch((error) => {
                    this.handleError(error);
                  });
            })
            .catch((error) => {
              this.handleError(error);
            });
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    refreshFleetStatus: function (silent) {
      this.clearError();

      if(!silent) {
        this.startPageLoading();
      }
      ApiService.post(`/nb/transaction-fleet/refresh`, {
        transactionId: this.transactionId
      })
          .then(({ data }) => {
            const fleetInfo = data.result;
            this.$set(this.appData, 'fleetInfo', fleetInfo);

            if(this.fleetLookupRunning || this.fleetExtractionRunning) {
              setTimeout(() => {
                this.refreshFleetStatus(true)
              }, 3000);
            }
            else {
              this.refreshBusinessLocations();
            }
            this.stopPageLoading();
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    refreshBusinessLocations: function () {
      ApiService.get(`/nb/transaction-business-locations`, { params: { transactionId: this.transactionId }})
          .then(({ data }) => {
            this.$set(this.appData, 'clientBranches', data.result || []);
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    saveBusinessLocations: function(locations) {
      this.clearError();

      this.startPageLoading();
      ApiService.post(`/nb/transaction-business-locations`, {
        transactionId: this.transactionId,
        locations: locations
      })
          .then((/*{ data }*/) => {
            this.stopPageLoading();
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    refreshClaimsHistoryStatus: function (silent) {
      this.clearError();

      if(!silent) {
        this.startPageLoading();
      }
      ApiService.get(`/nb/${this.transactionId}/claims-history-status`, {
        transactionId: this.transactionId
      })
          .then(({ data }) => {
            const claimsHistoryInfo = data.result;
            this.$set(this.appData, 'claimsHistoryInfo', claimsHistoryInfo);

            if(this.claimsHistoryExtractionRunning) {
              setTimeout(() => {
                this.refreshClaimsHistoryStatus(true)
              }, 5000);
              this.stopPageLoading();
            }
            else {
              if(this.uploadingClaimsHistory === true) {
                // Refresh claims history summary fields
                ApiService.get(`/nb/${this.transactionId}/claims-history-summary`, {
                  transactionId: this.transactionId
                })
                    .then(({ data }) => {
                      const claimsHistorySummary = data.result;
                      for(const key in claimsHistorySummary) {
                        this.$set(this.appData.transaction, key, claimsHistorySummary[key]);
                      }
                      this.uploadingClaimsHistory = false;
                      this.stopPageLoading();
                    })
                    .catch((error) => {
                      this.uploadingClaimsHistory = false;
                      this.handleError(error);
                    });
              }
              else {
                this.stopPageLoading();
              }
            }
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    viewUploadedClaimsHistory: function() {
      this.clearError();
      this.$set(this.appData.claimsHistoryInfo, 'downloadingClaimsHistory', true);
      ApiService.get(`/nb/${this.transactionId}/claims-history`)
          .then(({ data }) => {
            this.tempClaimsHistory = data.result || null;
            this.$set(this.appData.claimsHistoryInfo, 'downloadingClaimsHistory', false);
            this.$refs["claimsHistoryModal"].show();
          })
          .catch((error) => {
            this.$set(this.appData.claimsHistoryInfo, 'downloadingClaimsHistory', false);
            this.handleError(error);
          });
    },

    clearClaimsHistory: function () {
      this.tempClaimsHistory = null;
    },

    uploadClaimsHistoryFiles: function(selectedFiles) {
      this.clearError();

      this.startPageLoading();
      this.uploadingClaimsHistory = true;

      const formData = new FormData();
      for (const [i, file] of selectedFiles.entries()) {
        formData.append(`inputFiles[${i}]`, file);
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' }
      };

      ApiService.post(`/nb/${this.transactionId}/claims-history`, formData, config)
          .then(({ data }) => {
            const claimsHistoryInfo = data.result;
            this.$set(this.appData, 'claimsHistoryInfo', claimsHistoryInfo);
            this.stopPageLoading();

            this.refreshClaimsHistoryStatus(true);
          })
          .catch((error) => {
            this.errorAlert = `Something went wrong! Please try again.`;
            console.log(error);
          });
    },

    confirmNewFleetUpload: function (selectedFiles) {
      this.clearError();

      if(this.fleetExtractionRunning || this.fleetLookupRunning) {
        // Ask for confirmation about starting a new job
        const message = `Previous fleet processing job is still in progress. <br/><br/>Are you sure you want to start over?`;
        Swal.fire({
          titleText: `Fleet Processing`,
          html: `<p>${message}</p>`,
          icon: "warning",
          allowOutsideClick: false,
          allowEnterKey: false,
          confirmButtonText: 'Yes',
          showConfirmButton: true,
          showCancelButton: true,
          showCloseButton: false
        })
            .then(result => {
              if(result && result.isConfirmed) {
                this.uploadFleetFiles(selectedFiles);
              }
            });
      }
      else {
        this.uploadFleetFiles(selectedFiles);
      }
    },

    uploadFleetFiles: function(selectedFiles) {
      this.clearError();

      this.startPageLoading();
      const formData = new FormData();
      for (const [i, file] of selectedFiles.entries()) {
        formData.append(`inputFiles[${i}]`, file);
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' }
      };

      ApiService.post(`/fleet-upload/transaction/${this.transactionId}`, formData, config)
          .then(({ data }) => {
            const fleetInfo = data.result;
            this.$set(this.appData, 'fleetInfo', fleetInfo);
            this.stopPageLoading();
            this.refreshFleetStatus(true);
          })
          .catch((error) => {
            this.errorAlert = `Something went wrong! Please try again.`;
            console.log(error);
          });
    },

    downloadTransactionFleet: function() {
      this.clearError();
      this.$set(this.appData.fleetInfo, 'downloadingTransactionFleet', true);

      ApiService.get(`/nb/transaction-fleet/download`, { params: { transactionId: this.transactionId }})
        .then(({ data }) => {
          const fleetCsvInfo = data.result;
          if(fleetCsvInfo) {
            this.$set(this.appData.fleetInfo, 'incompleteCount', fleetCsvInfo.incompleteCount);

            const url = window.URL.createObjectURL(new Blob([fleetCsvInfo.fleetCsv]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Transaction_Fleet.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          else {
            this.setError(`Fleet data could not be downloaded. Please try again.`);
          }
          this.$set(this.appData.fleetInfo, 'downloadingTransactionFleet', false);
        })
        .catch((error) => {
          this.$set(this.appData.fleetInfo, 'downloadingTransactionFleet', false);
          this.handleError(error);
        });
    },

    saveTransactionFleet: function(newFleet) {
      this.clearError();
      this.startPageLoading();

      ApiService.post(`/nb/transaction-fleet`, {
        transactionId: this.transactionId,
        fleet: newFleet
      })
        .then(({ data }) => {
          const fleetInfo = data.result;
          this.$set(this.appData, 'fleetInfo', fleetInfo);

          this.stopPageLoading();
          this.refreshFleetStatus(true);
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    calculatePremiums: function() {
      this.clearError();
      if (this.isWizardReadOnly || !this.appData.transaction.K_RequiredExcess) {
          return;
      }

      if(this.claimsHistoryExtractionRunning) {
        this.setError('Please wait for the Claims History to be extracted before calculating premiums.');
        return;
      }
      if(this.fleetExtractionRunning || this.fleetLookupRunning) {
        this.setError('Please wait for the Fleet to be processed before calculating premiums.');
        return;
      }

      this.startPageLoading();
      this.$set(this.appData, 'uwApprovalsDataValid', false);

      // If alternateExcess1 is null and related quote exists - delete it
      if(!this.appData.transaction.K_AlternateExcess1 && _.nth(this.appData.quotes, 1)) {
        // Only keep quotes[0] i.e. requiredExcessQuote and delete all other quotes
        this.appData.quotes.splice(1, this.appData.quotes.length-1);
      }
      // If alternateExcess2 is null and related quote exists - delete it
      else if(this.appData.transaction.K_AlternateExcess1 && !this.appData.transaction.K_AlternateExcess2) {
        // Only keep quotes[0 & 1] delete all other quotes
        this.appData.quotes.splice(2, 1);
      }

      this.saveToServer()
        .then(() => {
          const txn = this.appData.transaction;

          if(txn.K_RequiredExcess) {
            /** Required Excess Quote */
            if(!this.isQuoteUwOverridden(0)) {
              const quote = _.nth(this.appData.quotes, 0);
              const quoteCustomData = this.getQuoteOverriddenData(quote);
              quoteCustomData.K_Excess = txn.K_RequiredExcess;

              this.calculateQuoteRating(quoteCustomData)
                  .then(({ data }) => {
                    this.$set(this.appData.quotes, 0, data.result);
                    this.stopPageLoading();
                  })
                  .catch((error) => {
                    this.handleError(error);
                  });
            }
            else {
              this.stopPageLoading();
            }

            /** Alternate Excess 1 Quote */
            if(this.appData.transaction.K_AlternateExcess1 && !this.isQuoteUwOverridden(1)) {
              const quote = _.nth(this.appData.quotes, 1);
              const quoteCustomData = this.getQuoteOverriddenData(quote);
              quoteCustomData.K_Excess = txn.K_AlternateExcess1;

              if(quote) {
                this.$set(quote, 'calculatingPremiums', true);
              }
              this.calculateQuoteRating(quoteCustomData)
                  .then(({ data }) => {
                    this.$set(this.appData.quotes, 1, data.result);
                  })
                  .catch((error) => {
                    if(quote) {
                      this.$set(quote, 'calculatingPremiums', false);
                    }
                    this.handleError(error);
                  });
            }

            /** Alternate Excess 2 Quote */
            if(this.appData.transaction.K_AlternateExcess2 && !this.isQuoteUwOverridden(2)) {
              const quote = _.nth(this.appData.quotes, 2);
              const quoteCustomData = this.getQuoteOverriddenData(quote);
              quoteCustomData.K_Excess = txn.K_AlternateExcess2;

              if(quote) {
                this.$set(quote, 'calculatingPremiums', true);
              }
              this.calculateQuoteRating(quoteCustomData)
                  .then(({ data }) => {
                    this.$set(this.appData.quotes, 2, data.result);
                  })
                  .catch((error) => {
                    if(quote) {
                      this.$set(quote, 'calculatingPremiums', false);
                    }
                    this.handleError(error);
                  });
            }
          }
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    copyFeesToOtherQuotes: function(quote) {
      this.clearError();
      for(const q of this.appData.quotes) {
        if(q.id === quote.id) {
          continue;
        }
        this.$set(q, 'K_PolicyEstablishmentFee', quote.K_PolicyEstablishmentFee);
        this.$set(q, 'K_CancellationFee', quote.K_CancellationFee);
        this.$set(q, 'K_MonthlyFee', quote.K_MonthlyFee);
        this.$set(q, 'K_EndorsementFee', quote.K_EndorsementFee);
        this.$set(q, 'K_AdministrationFees', quote.K_AdministrationFees);
        this.$set(q, 'K_CreditTermsDays', quote.K_CreditTermsDays);
        this.$set(q, 'K_MinimumNonRefundablePremium', quote.K_MinimumNonRefundablePremium);

        this.$set(q, 'K_NumberOfApplicableVehicles', quote.K_NumberOfApplicableVehicles);
        this.$set(q, 'K_FeePVPA', quote.K_FeePVPA);
        this.$set(q, 'K_TotalAnalyticsFee', quote.K_TotalAnalyticsFee);

        this.recalculateQuotePremiums(q);
      }
    },

    recalculateQuotePremiums: function(quote) {
      this.clearError();
      const quoteIndex = _.findIndex(this.appData.quotes, { 'id': quote.id });
      if(quoteIndex < 0) {
        throw `Invalid quote index!`;
      }

      this.$set(quote, 'calculatingPremiums', true);
      const decisionBasePremium = quote.K_UwDecisionBasePremium;
      this.saveToServer()
          .then(() => {
            const quoteCustomData = {
              K_Excess: quote.K_Excess,
              K_AdministrationFees: quote.K_AdministrationFees,
              K_PolicyEstablishmentFee: quote.K_PolicyEstablishmentFee,
              K_CancellationFee: quote.K_CancellationFee,
              K_TotalAnalyticsFee: quote.K_TotalAnalyticsFee,
              K_MonthlyFee: quote.K_MonthlyFee,
              K_EndorsementFee: quote.K_EndorsementFee,
              K_ExperienceWeightOverridePercent: quote.K_ExperienceWeightOverridePercent,
              K_UwOverrideComments: quote.K_UwOverrideComments,
              K_MarketAdjustmentPercent: quote.K_MarketAdjustmentPercent,
              K_MarketAdjustmentComments: quote.K_MarketAdjustmentComments,
              K_UwDecisionBasePremium: decisionBasePremium
            };

            this.calculateQuoteRating(quoteCustomData)
                .then(({ data }) => {
                  const rated = data.result;
                  if (decisionBasePremium !== null && decisionBasePremium !== undefined && decisionBasePremium !== '') {
                    const decisionCents = Math.round(Number(decisionBasePremium) * 100);
                    const baseCents = Math.round(Number(rated && rated.K_BasePremium) * 100);
                    if (!Number.isFinite(decisionCents) || !Number.isFinite(baseCents) || decisionCents !== baseCents) {
                      this.$set(quote, 'calculatingPremiums', false);
                      this.handleError(
                        `UW decision base premium verification failed: rated Base Premium `
                        + `(${rated && rated.K_BasePremium}) does not equal the decision (${decisionBasePremium}) to the cent.`
                      );
                      return;
                    }
                  }
                  if (rated) {
                    this.$set(rated, 'K_UwDecisionBasePremium', decisionBasePremium);
                  }
                  this.$set(this.appData.quotes, quoteIndex, rated);
                })
                .catch((error) => {
                  this.$set(quote, 'calculatingPremiums', false);
                  this.handleError(error);
                });
          })
          .catch((error) => {
            this.$set(quote, 'calculatingPremiums', false);
            this.handleError(error);
          });
    },

    getQuoteOverriddenData: function (quote) {
      const txn = this.appData.transaction;
      return {
        K_AdministrationFees: quote ? quote.K_AdministrationFees : txn.K_AdministrationFees,
        K_PolicyEstablishmentFee: quote ? quote.K_PolicyEstablishmentFee : txn.K_PolicyEstablishmentFee,
        K_TotalAnalyticsFee: quote ? quote.K_TotalAnalyticsFee : txn.K_TotalAnalyticsFee,
        K_MonthlyFee: quote ? quote.K_MonthlyFee : txn.K_MonthlyFee,
        K_ExperienceWeightOverridePercent: quote ? quote.K_ExperienceWeightOverridePercent : txn.K_ExperienceWeightOverridePercent,
        K_UwOverrideComments: quote ? quote.K_UwOverrideComments : txn.K_UwOverrideComments,
        K_MarketAdjustmentPercent: quote ? quote.K_MarketAdjustmentPercent : txn.K_MarketAdjustmentPercent,
        K_MarketAdjustmentComments: quote ? quote.K_MarketAdjustmentComments : txn.K_MarketAdjustmentComments
      };
    },

    calculateQuoteRating: function (quoteCustomData) {
      return ApiService.post("/nb/quote-rating", {
        transactionId: this.transactionId,
        quoteCustomData: quoteCustomData
      });
    },

    savePremiumPage: function() {
      this.clearError();

      this.startPageLoading();
      this.saveToServer()
          .then(() => {
            this.appDataString = stringify(this.appData);

            ApiService.post("/nb/premium-page-pdf", {
              transactionId: this.transactionId
            })
              .then(({ data }) => {
                this.stopPageLoading();
              })
              .catch((error) => {
                this.handleError(error);
              });
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    reloadClaimsHistoryFromClaimsCRM: function() {
      this.clearError();
      if (this.isWizardReadOnly) {
        return;
      }

      this.startPageLoading();

      this.saveToServer()
          .then(() => {
            ApiService.post("/nb/reload-claims-history", {
              transactionId: this.transactionId
            })
              .then(() => {
                this.refreshAppData()
                  .then(({ data }) => {
                    this.appData = data.result;
                    this.appDataString = stringify(this.appData);

                    this.calculateAllTotals();

                    this.stopPageLoading();
                  })
                  .catch((error) => {
                    this.handleError(error);
                  });
              })
              .catch((error) => {
                this.handleError(error);
              });
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    refreshUwApprovals: function() {
      this.clearError();
      if (this.isWizardReadOnly) {
        return;
      }

      // Show page loading
      this.startPageLoading();

      this.saveToServer()
        .then(() => {
          ApiService.post("/nb/uw-approvals", {
            transactionId: this.transactionId
          })
            .then(({ data }) => {
              this.$set(this.appData, 'uwApprovals', data.result || []);
              this.$set(this.appData, 'uwApprovalsDataValid', true);

              this.appDataString = stringify(this.appData);

              this.stopPageLoading();
            })
            .catch((error) => {
              this.appData.uwApprovalsDataValid = true;
              this.handleError(error);
            });
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    approveApproval: function (approval) {
      this.clearError();
      if (this.isWizardReadOnly) {
        return;
      }

      this.startPageLoading();
      this.uploadApprovalAttachments(approval)
          .then(() => {
            ApiService.post("/nb/uw-approval/approve", {
              transactionId: this.transactionId,
              approvalId: approval.id,
              comments: approval.K_Comments
            })
                .then(({ data }) => {
                  this.$set(this.appData, 'uwApprovals', data.result || []);
                  this.$set(this.appData, 'uwApprovalsDataValid', true);

                  this.appDataString = stringify(this.appData);

                  this.stopPageLoading();
                })
                .catch((error) => {
                  this.handleError(error);
                });
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    rejectApproval: function (approval) {
      this.clearError();
      if (this.isWizardReadOnly) {
        return;
      }

      this.startPageLoading();
      this.uploadApprovalAttachments(approval)
          .then(() => {
            ApiService.post("/nb/uw-approval/reject", {
              transactionId: this.transactionId,
              approvalId: approval.id,
              comments: approval.K_Comments
            })
              .then(({ data }) => {
                this.$set(this.appData, 'uwApprovals', data.result || []);
                this.$set(this.appData, 'uwApprovalsDataValid', true);

                this.appDataString = stringify(this.appData);

                this.stopPageLoading();
              })
              .catch((error) => {
                this.handleError(error);
              });
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    uploadApprovalAttachments: function (approval) {
      if(Array.isArray(approval.selectedFiles) && approval.selectedFiles.length > 0) {
        const formData = new FormData();
        for(const [i, selectedFile] of approval.selectedFiles.entries()) {
          formData.append(`attachments[${i}]`, selectedFile);
        }
        const config = {
          headers: {
            "Content-Type": "multipart/form-data",
          }
        };

        return ApiService.post(`/nb/uw-approval/${approval.id}/attachments`, formData, config);
      }
      else {
        // Just return an empty resolved promise as no saving is needed.
        return Promise.resolve();
      }
    },

    calculateAllTotals: function() {
      this.calculateFleetHistoryTotals();
      this.calculateVehicleTypesTotals();
      this.calculateBusinessActivityPercentTotal();
      this.calculatePostcodeDistributionTotal();
      this.calculateClaimsHistoryTotals();

      if(Array.isArray(this.appData?.quotes)) {
        for(const quote of this.appData.quotes) {
          this.calculateVehiclePremiumTotals(quote);
          this.calculateStateSummaryTotals(quote);
          this.calculateTotalFees(quote);
        }
      }
    },

    calculateFleetHistoryTotals: function () {
      if (this.appData && this.appData.transaction) {
        const txn = this.appData.transaction;

        if(txn.K_ComprehensiveCurrentYear || txn.K_TppdCurrentYear) {
          this.$set(txn, 'K_TotalFleetSizeCurrentYear', this.numberOrZero(txn.K_ComprehensiveCurrentYear) + this.numberOrZero(txn.K_TppdCurrentYear));
        }
        else {
          this.$set(txn, 'K_TotalFleetSizeCurrentYear', null);
        }

        if(txn.K_Comprehensive1YearAgo || txn.K_Tppd1YearAgo) {
          this.$set(txn, 'K_TotalFleetSize1YearAgo', this.numberOrZero(txn.K_Comprehensive1YearAgo) + this.numberOrZero(txn.K_Tppd1YearAgo));
        }
        else {
          this.$set(txn, 'K_TotalFleetSize1YearAgo', null);
        }

        if(txn.K_Comprehensive2YearsAgo || txn.K_Tppd2YearsAgo) {
          this.$set(txn, 'K_TotalFleetSize2YearsAgo', this.numberOrZero(txn.K_Comprehensive2YearsAgo) + this.numberOrZero(txn.K_Tppd2YearsAgo));
        }
        else {
          this.$set(txn, 'K_TotalFleetSize2YearsAgo', null);
        }

        if(txn.K_Comprehensive3YearsAgo || txn.K_Tppd3YearsAgo) {
          this.$set(txn, 'K_TotalFleetSize3YearsAgo', this.numberOrZero(txn.K_Comprehensive3YearsAgo) + this.numberOrZero(txn.K_Tppd3YearsAgo));
        }
        else {
          this.$set(txn, 'K_TotalFleetSize3YearsAgo', null);
        }

        if(txn.K_Comprehensive4YearsAgo || txn.K_Tppd4YearsAgo) {
          this.$set(txn, 'K_TotalFleetSize4YearsAgo', this.numberOrZero(txn.K_Comprehensive4YearsAgo) + this.numberOrZero(txn.K_Tppd4YearsAgo));
        }
        else {
          this.$set(txn, 'K_TotalFleetSize4YearsAgo', null);
        }
      }
    },

    calculateVehicleTypesTotals: function() {
      const txn = this.appData?.transaction;
      if (txn && Array.isArray(txn.K_VehiclesSummary)) {
        let totalNumberOfVehicles = 0;
        let totalComprehensiveVehicles = 0;
        let totalMarketValue = 0;

        const vehiclesSummary = txn.K_VehiclesSummary;
        vehiclesSummary.forEach(vehicleCategory => {
          // Total number of vehicles
          const numberOfVehicles = vehicleCategory.K_NumberOfVehicles ? Number(vehicleCategory.K_NumberOfVehicles) : 0;
          totalNumberOfVehicles = totalNumberOfVehicles + numberOfVehicles;

          // Total number of comprehensive vehicles
          if(vehicleCategory.K_TypeOfCover === 'Section 1 & 2') {
            totalComprehensiveVehicles = totalComprehensiveVehicles + numberOfVehicles;
          }

          // Total market value
          if (vehicleCategory.K_TotalMarketValue) {
            totalMarketValue = totalMarketValue + Number(vehicleCategory.K_TotalMarketValue);
          }
        });

        this.$set(this.appData.transaction, 'K_TotalNumberOfVehicles', totalNumberOfVehicles || null);
        const avgMarketValue = totalComprehensiveVehicles ? (totalMarketValue / totalComprehensiveVehicles).toFixed(2) : null;
        this.$set(this.appData.transaction, 'K_AverageTotalMarketValue', avgMarketValue);
      }
    },

    calculateVehiclePremiumTotals: function(quote) {
      if (quote && Array.isArray(quote.K_VehiclesAndPremiums)) {
        let totalNumberOfVehicles = 0;
        let totalMarketValue = 0;
        let totalBasePremium = 0;
        let totalBasePremiumUwOverride = 0;
        let totalRatePerVehicle = 0;

        const vehiclesSummary = quote.K_VehiclesAndPremiums;
        vehiclesSummary.forEach(vehicleCategory => {
          const numberOfVehicles = vehicleCategory.K_NumberOfVehicles ? Number(vehicleCategory.K_NumberOfVehicles) : 0;

          /** Total number of branches */
          totalNumberOfVehicles = totalNumberOfVehicles + numberOfVehicles;

          /** Total of Total-Market-Value */
          if (vehicleCategory.K_TotalMarketValue) {
            totalMarketValue = totalMarketValue + Number(vehicleCategory.K_TotalMarketValue);
          }

          /** Total Base Premium per Vehicle */
          if (vehicleCategory.K_BasePremiumPerVehicle) {
            totalBasePremium = totalBasePremium + (numberOfVehicles * Number(vehicleCategory.K_BasePremiumPerVehicle));
          }

          /** Total Base Premium per Vehicle UW Override*/
          if (vehicleCategory.K_BasePremiumPerVehicleUwOverride) {
            totalBasePremiumUwOverride = totalBasePremiumUwOverride + (numberOfVehicles * Number(vehicleCategory.K_BasePremiumPerVehicleUwOverride));
          }

          /** Total Rate per Vehicle} */
          if (vehicleCategory.K_RatePerVehicle) {
            totalRatePerVehicle = totalRatePerVehicle + (numberOfVehicles * Number(vehicleCategory.K_RatePerVehicle));
          }
        });

        this.$set(quote, 'K_TotalNumberOfVehicles', totalNumberOfVehicles || null);
        this.$set(quote, 'K_AverageTotalMarketValue', totalNumberOfVehicles ? (totalMarketValue / totalNumberOfVehicles).toFixed(2) : null);
        this.$set(quote, 'K_AverageBasePremiumPerVehicle', totalNumberOfVehicles ? (totalBasePremium / totalNumberOfVehicles).toFixed(2) : null);
        this.$set(quote, 'K_AverageBasePremiumPerVehicleUwOverride', totalNumberOfVehicles ? (totalBasePremiumUwOverride / totalNumberOfVehicles).toFixed(2) : null);
        this.$set(quote, 'K_AverageRatePerVehicle', totalNumberOfVehicles ? (totalRatePerVehicle / totalNumberOfVehicles).toFixed(2) : null);
      }
    },

    calculateStateSummaryTotals: function(quote) {
      let totalBasePremium = 0;
      let totalEslFsl = 0;
      let totalStampDuty = 0;
      let totalGst = 0;
      let totalAnnualPremium = 0;

      if (quote && Array.isArray(quote.K_StateCharges)) {
        quote.K_StateCharges.forEach(state => {
          if (state.K_BasePremium) {
            totalBasePremium = totalBasePremium + Number(state.K_BasePremium);
          }
          if (state.K_EslFsl) {
            totalEslFsl = totalEslFsl + Number(state.K_EslFsl);
          }
          if (state.K_StampDuty) {
            totalStampDuty = totalStampDuty + Number(state.K_StampDuty);
          }
          if (state.K_Gst) {
            totalGst = totalGst + Number(state.K_Gst);
          }
          if (state.K_AnnualPremium) {
            totalAnnualPremium = totalAnnualPremium + Number(state.K_AnnualPremium);
          }
        });
        this.$set(quote, 'stateSummaryTotals', {
          totalBasePremium: totalBasePremium.toFixed(2),
          totalEslFsl: totalEslFsl.toFixed(2),
          totalStampDuty: totalStampDuty.toFixed(2),
          totalGst: totalGst.toFixed(2),
          totalAnnualPremium: totalAnnualPremium.toFixed(2)
        });
      }
    },

    calculateBusinessActivityPercentTotal: function() {
      let businessActivitiesPercentTotal = 0;
      if (this.appData && this.appData.transaction) {
        let businessActivities = this.appData.transaction.K_BusinessActivitiesJson;
        if (Array.isArray(businessActivities)) {
          businessActivities.forEach(ba => {
            if (ba.percentage) {
              businessActivitiesPercentTotal = businessActivitiesPercentTotal + Number(ba.percentage);
            }
          });
        }
        this.$set(this.appData.transaction, 'businessActivitiesPercentTotal', this.round(businessActivitiesPercentTotal));
      }
    },

    calculatePostcodeDistributionTotal: function () {
      let pdTotal = 0;
      let pdVehNumTotal = 0;
      if (this.appData && this.appData.transaction) {
        const pdJson = this.appData.transaction.K_PostcodeDistributionJson;
        if (Array.isArray(pdJson)) {
          pdJson.forEach(pd => {
            if (pd.percentage) {
              pdTotal = pdTotal + Number(pd.percentage);
            }
            if (pd.numberOfVehicles) {
              pdVehNumTotal = pdVehNumTotal + Number(pd.numberOfVehicles);
            }
          });
        }
        this.$set(this.appData.transaction, 'pdTotal', this.round(pdTotal));
        this.$set(this.appData.transaction, 'pdVehNumTotal', pdVehNumTotal);
      }
    },

    calculateClaimsHistoryTotals: function () {
      if (this.appData && this.appData.transaction) {
        const txn = this.appData.transaction;

        const claimsCountCurrentYear =
              this.toNumber(txn.K_ClaimsCountWorkingLossesCurrentYear)
            + this.toNumber(txn.K_ClaimsCountLargeLossesCurrentYear)
            + this.toNumber(txn.K_ClaimsCountNaturalPerilLossesCurrentYear);
        const niCurrentYear =
            this.toNumber(txn.K_NetIncurredWorkingLossesCurrentYear)
          + this.toNumber(txn.K_NetIncurredLargeLossesCurrentYear)
          + this.toNumber(txn.K_NetIncurredNaturalPerilLossesCurrentYear);

        const claimsCount1YearAgo =
              this.toNumber(txn.K_ClaimsCountWorkingLosses1YearAgo)
            + this.toNumber(txn.K_ClaimsCountLargeLosses1YearAgo)
            + this.toNumber(txn.K_ClaimsCountNaturalPerilLosses1YearAgo);
        const ni1YearAgo =
            this.toNumber(txn.K_NetIncurredWorkingLosses1YearAgo)
          + this.toNumber(txn.K_NetIncurredLargeLosses1YearAgo)
          + this.toNumber(txn.K_NetIncurredNaturalPerilLosses1YearAgo);

        const claimsCount2YearsAgo =
              this.toNumber(txn.K_ClaimsCountWorkingLosses2YearsAgo)
            + this.toNumber(txn.K_ClaimsCountLargeLosses2YearsAgo)
            + this.toNumber(txn.K_ClaimsCountNaturalPerilLosses2YearsAgo);
        const ni2YearsAgo =
            this.toNumber(txn.K_NetIncurredWorkingLosses2YearsAgo)
          + this.toNumber(txn.K_NetIncurredLargeLosses2YearsAgo)
          + this.toNumber(txn.K_NetIncurredNaturalPerilLosses2YearsAgo);

        const claimsCount3YearsAgo =
              this.toNumber(txn.K_ClaimsCountWorkingLosses3YearsAgo)
            + this.toNumber(txn.K_ClaimsCountLargeLosses3YearsAgo)
            + this.toNumber(txn.K_ClaimsCountNaturalPerilLosses3YearsAgo);
        const ni3YearsAgo =
            this.toNumber(txn.K_NetIncurredWorkingLosses3YearsAgo)
          + this.toNumber(txn.K_NetIncurredLargeLosses3YearsAgo)
          + this.toNumber(txn.K_NetIncurredNaturalPerilLosses3YearsAgo);

        const claimsCount4YearsAgo =
            this.toNumber(txn.K_ClaimsCountWorkingLosses4YearsAgo)
            + this.toNumber(txn.K_ClaimsCountLargeLosses4YearsAgo)
            + this.toNumber(txn.K_ClaimsCountNaturalPerilLosses4YearsAgo);
        const ni4YearsAgo =
            this.toNumber(txn.K_NetIncurredWorkingLosses4YearsAgo)
            + this.toNumber(txn.K_NetIncurredLargeLosses4YearsAgo)
            + this.toNumber(txn.K_NetIncurredNaturalPerilLosses4YearsAgo);

        this.$set(this.appData.transaction, 'claimsCountCurrentYear', claimsCountCurrentYear);
        this.$set(this.appData.transaction, 'niCurrentYear', this.round(niCurrentYear));

        this.$set(this.appData.transaction, 'claimsCount1YearAgo', claimsCount1YearAgo);
        this.$set(this.appData.transaction, 'ni1YearAgo', this.round(ni1YearAgo));

        this.$set(this.appData.transaction, 'claimsCount2YearsAgo', claimsCount2YearsAgo);
        this.$set(this.appData.transaction, 'ni2YearsAgo', this.round(ni2YearsAgo));

        this.$set(this.appData.transaction, 'claimsCount3YearsAgo', claimsCount3YearsAgo);
        this.$set(this.appData.transaction, 'ni3YearsAgo', this.round(ni3YearsAgo));

        this.$set(this.appData.transaction, 'claimsCount4YearsAgo', claimsCount4YearsAgo);
        this.$set(this.appData.transaction, 'ni4YearsAgo', this.round(ni4YearsAgo));

        /** Calculate TOTALS row */

        const claimsCountWorkingLossesTotal =
            this.toNumber(txn.K_ClaimsCountWorkingLossesCurrentYear)
            + this.toNumber(txn.K_ClaimsCountWorkingLosses1YearAgo)
            + this.toNumber(txn.K_ClaimsCountWorkingLosses2YearsAgo)
            + this.toNumber(txn.K_ClaimsCountWorkingLosses3YearsAgo)
            + this.toNumber(txn.K_ClaimsCountWorkingLosses4YearsAgo);
        const netIncurredWorkingLossesTotal = this.round(
            this.toNumber(txn.K_NetIncurredWorkingLossesCurrentYear)
            + this.toNumber(txn.K_NetIncurredWorkingLosses1YearAgo)
            + this.toNumber(txn.K_NetIncurredWorkingLosses2YearsAgo)
            + this.toNumber(txn.K_NetIncurredWorkingLosses3YearsAgo)
            + this.toNumber(txn.K_NetIncurredWorkingLosses4YearsAgo));
        this.$set(this.appData.transaction, 'claimsCountWorkingLossesTotal', claimsCountWorkingLossesTotal);
        this.$set(this.appData.transaction, 'niWorkingLossesTotal', this.round(netIncurredWorkingLossesTotal));

        const claimsCountNaturalPerilLossesTotal =
            this.toNumber(txn.K_ClaimsCountNaturalPerilLossesCurrentYear)
            + this.toNumber(txn.K_ClaimsCountNaturalPerilLosses1YearAgo)
            + this.toNumber(txn.K_ClaimsCountNaturalPerilLosses2YearsAgo)
            + this.toNumber(txn.K_ClaimsCountNaturalPerilLosses3YearsAgo)
            + this.toNumber(txn.K_ClaimsCountNaturalPerilLosses4YearsAgo);
        const netIncurredNaturalPerilLossesTotal = this.round(
            this.toNumber(txn.K_NetIncurredNaturalPerilLossesCurrentYear)
            + this.toNumber(txn.K_NetIncurredNaturalPerilLosses1YearAgo)
            + this.toNumber(txn.K_NetIncurredNaturalPerilLosses2YearsAgo)
            + this.toNumber(txn.K_NetIncurredNaturalPerilLosses3YearsAgo)
            + this.toNumber(txn.K_NetIncurredNaturalPerilLosses4YearsAgo));
        this.$set(this.appData.transaction, 'claimsCountNaturalPerilLossesTotal', claimsCountNaturalPerilLossesTotal);
        this.$set(this.appData.transaction, 'niNaturalPerilLossesTotal', this.round(netIncurredNaturalPerilLossesTotal));

        const claimsCountLargeLossesTotal =
            this.toNumber(txn.K_ClaimsCountLargeLossesCurrentYear)
            + this.toNumber(txn.K_ClaimsCountLargeLosses1YearAgo)
            + this.toNumber(txn.K_ClaimsCountLargeLosses2YearsAgo)
            + this.toNumber(txn.K_ClaimsCountLargeLosses3YearsAgo)
            + this.toNumber(txn.K_ClaimsCountLargeLosses4YearsAgo);
        const netIncurredLargeLossesTotal = this.round(
            this.toNumber(txn.K_NetIncurredLargeLossesCurrentYear)
            + this.toNumber(txn.K_NetIncurredLargeLosses1YearAgo)
            + this.toNumber(txn.K_NetIncurredLargeLosses2YearsAgo)
            + this.toNumber(txn.K_NetIncurredLargeLosses3YearsAgo)
            + this.toNumber(txn.K_NetIncurredLargeLosses4YearsAgo));
        this.$set(this.appData.transaction, 'claimsCountLargeLossesTotal', claimsCountLargeLossesTotal);
        this.$set(this.appData.transaction, 'niLargeLossesTotal', this.round(netIncurredLargeLossesTotal));

        const claimsCountTotal = claimsCountCurrentYear + claimsCount1YearAgo + claimsCount2YearsAgo + claimsCount3YearsAgo + claimsCount4YearsAgo;
        const niTotal = this.round(niCurrentYear + ni1YearAgo + ni2YearsAgo + ni3YearsAgo + ni4YearsAgo);
        this.$set(this.appData.transaction, 'claimsCountTotal', claimsCountTotal);
        this.$set(this.appData.transaction, 'niTotal', this.round(niTotal));
      }
    },

    toNumber: function(number) {
      const converted = Number(number);
      if(_.isFinite(converted) && !_.isNaN(converted)) {
        return converted;
      }
      return 0;
    },

    calculateTotalFees: function (quote) {
      if(quote) {
        if(this.isBrandFuse) {
          this.calculateAnalyticsFee(quote);
        }

        let totalFees = 0;
        if (quote.K_PolicyEstablishmentFee) {
          totalFees = totalFees + _.toNumber(quote.K_PolicyEstablishmentFee);
        }
        if (quote.K_AdministrationFees) {
          totalFees = totalFees + _.toNumber(quote.K_AdministrationFees);
        }
        if (this.isBrandFuse && quote.K_TotalAnalyticsFee) {
          totalFees = totalFees + _.toNumber(quote.K_TotalAnalyticsFee);
        }
        if(quote.K_PaymentMethod === 'Monthly Premium' && quote.K_MonthlyFee) {
          const totalMonthlyFees = _.toNumber(quote.K_MonthlyFee) * 12;
          totalFees = totalFees + totalMonthlyFees;
        }

        this.$set(quote, 'K_TotalFees', totalFees);

        if(quote.K_UwPremiumOverride) {
          this.calculatePremiumTotalsUwOverride(quote);
        }
      }
    },

    calculateAnalyticsFee: function (quote) {
      if (quote) {
        let analyticsFee = null;
        if(!isNaN(quote.K_NumberOfApplicableVehicles) && !isNaN(quote.K_FeePVPA)) {
          analyticsFee = Number(quote.K_NumberOfApplicableVehicles) * Number(quote.K_FeePVPA);
          analyticsFee = this.round(analyticsFee);
        }
        this.$set(quote, 'K_TotalAnalyticsFee', analyticsFee);
      }
    },

    calculatePremiumTotalsUwOverride: function(quote) {
      if (quote) {
        /** Calculate Sub-Total */
        let subTotal = 0;
        if (quote.K_BasePremium) {
          subTotal = subTotal + _.toNumber(quote.K_BasePremium);
        }
        if (quote.K_EslFsl) {
          subTotal = subTotal + _.toNumber(quote.K_EslFsl);
        }
        if (quote.K_UwGst) {
          subTotal = subTotal + _.toNumber(quote.K_UwGst);
        }
        if (quote.K_StampDuty) {
          subTotal = subTotal + _.toNumber(quote.K_StampDuty);
        }

        /** Calculate Total Annual Premium */
        let totalAnnualPremium = subTotal;
        if (_.isNumber(quote.K_TotalFees)) {
          totalAnnualPremium = totalAnnualPremium + _.toNumber(quote.K_TotalFees);
          quote.K_TotalFeeGst = this.calculateGst(quote.K_TotalFees);
        }
        else {
          quote.K_TotalFeeGst = null;
        }
        if (quote.K_TotalFeeGst) {
          totalAnnualPremium = totalAnnualPremium + _.toNumber(quote.K_TotalFeeGst);
        }

        /** Calculate Net Payable to Rentsure */
        let netPayableToRentsure = totalAnnualPremium;
        if (_.isNumber(quote.K_BrokerCommission)) {
          netPayableToRentsure = netPayableToRentsure - _.toNumber(quote.K_BrokerCommission);
          // Calculate Broker-Commission-GST
          quote.K_BrokerCommissionGst = this.calculateGst(quote.K_BrokerCommission);
        }
        else {
          quote.K_BrokerCommissionGst = null;
        }
        if (quote.K_BrokerCommissionGst) {
          netPayableToRentsure = netPayableToRentsure - _.toNumber(quote.K_BrokerCommissionGst);
        }

        quote.K_SubTotal = subTotal.toFixed(2);
        quote.K_TotalAnnualPremium = totalAnnualPremium.toFixed(2);
        quote.K_NetPayableToRentsure = netPayableToRentsure.toFixed(2);
      }
    },

    round: function (number) {
      if(!_.isNil(number)) {
        return Math.round(Number(number) * 100 + Number.EPSILON)/100;
      }
      return null;
    },

    calculateGst: function (currencyValue) {
      if (!_.isNil(currencyValue)) {
        return this.round(Number(currencyValue) * 0.1);
      }
      return null;
    },

    numberOrZero: function (value) {
      return !_.isNil(value) ? Number(value) : 0;
    },

    setError: function (message) {
      KTUtil.scrollTop();
      this.errorAlert = message;
    },

    clearError: function () {
      this.errorAlert = null;
      this.nextButtonError = null;
    },

    handleError: function(errorObj) {
      this.stopPageLoading();
      let errorMsg = null;

      if(errorObj) {
        if(_.isString(errorObj)) {
          errorMsg = errorObj;
        }
        else {
          let responseStatus = _.get(errorObj, "response.status");
          if (responseStatus === 401 || (errorObj.message && errorObj.message.includes("status code 401"))) {
            errorMsg = "This session is invalid or expired. Please close this window.";
          }
          else {
            let responseResult = _.get(errorObj, "response.data.result");
            if (_.isString(responseResult)) {
              let applicationErrorPrefix = "UI_ERROR:";

              if (responseResult.startsWith(applicationErrorPrefix)) {
                errorMsg = responseResult.substring(applicationErrorPrefix.length, responseResult.length);
              }
            }
          }
        }
      }
      if (!errorMsg) {
        errorMsg = "An unexpected error has occurred.";
      }

      this.setError(errorMsg);
      KTUtil.scrollTop();
    },

    startPageLoading: function () {
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
    },

    stopPageLoading: function () {
      this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
    },
  },
};
</script>
